import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, PieChart, Pie, Cell } from 'recharts';
import apiService from '../services/api';
import './Dashboard.css';
import GlobalHeader from './GlobalHeader';

// Assessment Summary Modal Component
const AssessmentSummaryModal = ({ isOpen, onClose, title, employees, userRole, currentPage, setCurrentPage, itemsPerPage: initialItemsPerPage = 10 }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState(employees);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setItemsPerPage(initialItemsPerPage);
      setCurrentPage(1);
      document.body.style.overflow = 'unset';
    } else {
      document.body.style.overflow = 'hidden';
    }
  }, [isOpen, initialItemsPerPage, setCurrentPage]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = employees.filter(emp => 
        (emp.email && emp.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.name && emp.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.manager_email && emp.manager_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.department && emp.department.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredEmployees(filtered);
      setCurrentPage(1);
    } else {
      setFilteredEmployees(employees);
    }
  }, [searchTerm, employees, setCurrentPage]);

  const exportToCSV = () => {
    const headers = userRole === 'admin' 
      ? ['Email', 'Name', 'Manager Email', 'Department', 'Self CI', 'Manager CI']
      : ['Email', 'Name', 'Self CI', 'Manager CI'];
    
    const csvContent = [
      headers.join(','),
      ...filteredEmployees.map(emp => {
        const row = userRole === 'admin'
          ? [
              `"${emp.email || ''}"`,
              `"${emp.name || ''}"`,
              `"${emp.manager_email || ''}"`,
              `"${emp.department || ''}"`,
              emp.self_ci || 'N/A',
              emp.manager_ci || 'N/A'
            ]
          : [
              `"${emp.email || ''}"`,
              `"${emp.name || ''}"`,
              emp.self_ci || 'N/A',
              emp.manager_ci || 'N/A'
            ];
        return row.join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${title.replace(/\s+/g, '_')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{title}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={modalStyles.body}>
          {/* Search and Export Controls */}
          <div style={modalStyles.controls}>
            <div style={modalStyles.searchContainer}>
              <input
                type="text"
                placeholder={userRole === 'manager' ? "Search by email or name..." : "Search by email, name, manager, or department..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={modalStyles.searchInput}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <button
              onClick={exportToCSV}
              style={{
                ...modalStyles.exportButton,
                ...(filteredEmployees.length === 0 ? modalStyles.exportButtonDisabled : {})
              }}
              disabled={filteredEmployees.length === 0}
            >
              📥 Export CSV
            </button>
          </div>

          <div style={modalStyles.tableContainer}>
            {filteredEmployees.length === 0 ? (
              <div style={modalStyles.emptyState}>
                <p>No employees found matching your search.</p>
              </div>
            ) : (
              <table style={modalStyles.table}>
                <thead>
                  <tr style={modalStyles.tableHeader}>
                    <th style={modalStyles.tableHeaderCell}>Email</th>
                    <th style={modalStyles.tableHeaderCell}>Name</th>
                    {userRole === 'admin' && (
                      <>
                        <th style={modalStyles.tableHeaderCell}>Manager Email</th>
                        <th style={modalStyles.tableHeaderCell}>Department</th>
                      </>
                    )}
                    <th style={modalStyles.tableHeaderCell}>Self CI</th>
                    <th style={modalStyles.tableHeaderCell}>Manager CI</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEmployees.map((employee, index) => (
                    <tr key={index} style={modalStyles.tableRow}>
                      <td style={modalStyles.tableCell}>{employee.email}</td>
                      <td style={modalStyles.tableCell}>{employee.name}</td>
                      {userRole === 'admin' && (
                        <>
                          <td style={modalStyles.tableCell}>{employee.manager_email}</td>
                          <td style={modalStyles.tableCell}>{employee.department || 'N/A'}</td>
                        </>
                      )}
                      <td style={modalStyles.tableCell}>{employee.self_ci || 'N/A'}</td>
                      <td style={modalStyles.tableCell}>{employee.manager_ci || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {filteredEmployees.length > 0 && (
            <div style={modalStyles.paginationContainer}>
              <div style={modalStyles.paginationInfo}>
                Showing {startIndex + 1} to {Math.min(endIndex, filteredEmployees.length)} of {filteredEmployees.length} entries
                <span style={{ marginLeft: '16px', marginRight: '8px' }}>Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={modalStyles.rowsPerPageSelect}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {totalPages > 1 && (
                <div style={modalStyles.pagination}>
                  <button
                    style={{...modalStyles.pageButton, ...(currentPage === 1 ? modalStyles.disabledButton : {})}}
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>

                  <div style={modalStyles.pageNumbers}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            style={{
                              ...modalStyles.pageNumber,
                              ...(currentPage === page ? modalStyles.activePageNumber : {})
                            }}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} style={modalStyles.pageEllipsis}>...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    style={{...modalStyles.pageButton, ...(currentPage === totalPages ? modalStyles.disabledButton : {})}}
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom label component for Pie chart that truncates from end and shows tooltip
const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, percentage, maxLength = 25 }) => {
  const RADIAN = Math.PI / 180;
  // Position label at the middle radius of the pie slice (same as default)
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  // Truncate from the end (keep first letters, cut last letters)
  const displayName = name.length > maxLength 
    ? `${name.substring(0, maxLength)}...` 
    : name;
  
  return (
    <g>
      <title>{`${name}: ${percentage}%`}</title>
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={500}
      >
        {`${displayName}: ${percentage}%`}
      </text>
    </g>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [visualData, setVisualData] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [departmentData, setDepartmentData] = useState([]);
  const [availableCycles, setAvailableCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Assessment summary states
  const [assessmentSummary, setAssessmentSummary] = useState(null);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [showNotSubmittedModal, setShowNotSubmittedModal] = useState(false);
  const [submittedPage, setSubmittedPage] = useState(1);
  const [notSubmittedPage, setNotSubmittedPage] = useState(1);

  useEffect(() => {
    document.title = 'CR Capability Index - Dashboard';
  }, []);

  // Fetch available cycles on component mount
  useEffect(() => {
    const fetchCycles = async () => {
      try {
        const cyclesResponse = await apiService.fetchData('metadata?type=cycles');
        if (cyclesResponse && cyclesResponse.status === 'success' && cyclesResponse.data.length > 0) {
          // Sort cycles by CycleID DESC to get latest first
          const sortedCycles = [...cyclesResponse.data].sort((a, b) => (b.CycleID || 0) - (a.CycleID || 0));
          setAvailableCycles(sortedCycles);
          // Set latest cycle as default (first in the list since ordered by CycleID DESC)
          setSelectedCycle(sortedCycles[0]);
        }
      } catch (err) {
        console.error('Error fetching cycles:', err);
        // Continue with dashboard loading even if cycles fail to load
      }
    };

    fetchCycles();
  }, []);

  // Fetch dashboard data when selectedCycle changes
  useEffect(() => {
    if (selectedCycle) {
      const fetchDashboardData = async () => {
        try {
          setLoading(true);
          setError(null);

          // Get user role and email from session storage
          const userRole = sessionStorage.getItem('access_role') || 'user';
          const userEmail = sessionStorage.getItem('user_emailId');

          // Determine API endpoint based on role
          let summaryApiUrl = '';
          let visualApiUrl = '';
          let graphApiUrl = '';
          let departmentApiUrl = '';

          const cycleParam = `&cycle_id=${selectedCycle.CycleID}`;

          if (userRole === 'manager' && userEmail) {
            summaryApiUrl = `dashboard-card?type=manager&manager_email=${userEmail}${cycleParam}`;
            visualApiUrl = `dashboard-card?type=manager&manager_email=${userEmail}&visual_type=capability${cycleParam}`;
            graphApiUrl = `dashboard-card?type=manager&manager_email=${userEmail}&visual_type=graph${cycleParam}`;
            departmentApiUrl = `dashboard-card?type=manager&manager_email=${userEmail}&visual_type=department${cycleParam}`;
          } else if (userRole === 'admin') {
            summaryApiUrl = `dashboard-card?type=admin${cycleParam}`;
            visualApiUrl = `dashboard-card?type=admin&visual_type=capability${cycleParam}`;
            graphApiUrl = `dashboard-card?type=admin&visual_type=graph${cycleParam}`;
            departmentApiUrl = `dashboard-card?type=admin&visual_type=department${cycleParam}`;
          } else {
            // For regular users or if no role/email, use admin endpoint as fallback
            summaryApiUrl = `dashboard-card?type=admin${cycleParam}`;
            visualApiUrl = `dashboard-card?type=admin&visual_type=capability${cycleParam}`;
            graphApiUrl = `dashboard-card?type=admin&visual_type=graph${cycleParam}`;
            departmentApiUrl = `dashboard-card?type=admin&visual_type=department${cycleParam}`;
          }

          // Fetch summary data
          const summaryResponse = await apiService.fetchData(summaryApiUrl);
          if (summaryResponse && summaryResponse.status === 'success') {
            setDashboardData(summaryResponse.data);
          } else {
            throw new Error('Failed to fetch dashboard summary data');
          }

          // Fetch visual data for strengths/growth areas
          const visualResponse = await apiService.fetchData(visualApiUrl);
          if (visualResponse && visualResponse.status === 'success') {
            setVisualData(visualResponse.data);
          } else {
            throw new Error('Failed to fetch visual data');
          }

          // Fetch graph data for bar chart and spider chart
          const graphResponse = await apiService.fetchData(graphApiUrl);
          if (graphResponse && graphResponse.status === 'success') {
            setGraphData(graphResponse.data);
          } else {
            throw new Error('Failed to fetch graph data');
          }

          // Fetch department data for department-wise performance
          const departmentResponse = await apiService.fetchData(departmentApiUrl);
          if (departmentResponse && departmentResponse.status === 'success') {
            setDepartmentData(departmentResponse.data?.departments || []);
          } else {
            // Don't throw error for department data, just log and continue
            console.warn('Failed to fetch department data, using empty array');
            setDepartmentData([]);
          }

          // Fetch assessment summary data for the new cards
          let assessmentApiUrl = '';
          if (userRole === 'manager' && userEmail) {
            assessmentApiUrl = `manager-assessment-summary?manager_email=${userEmail}&cycle_id=${selectedCycle.CycleID}`;
          } else if (userRole === 'admin') {
            assessmentApiUrl = `global-assessment-summary?cycle_id=${selectedCycle.CycleID}`;
          }

          if (assessmentApiUrl) {
            try {
              const assessmentResponse = await apiService.fetchData(assessmentApiUrl);
              if (assessmentResponse && assessmentResponse.status === 'success') {
                setAssessmentSummary(assessmentResponse.data);
              }
            } catch (err) {
              console.error('Error fetching assessment summary:', err);
              // Don't fail the whole dashboard if assessment summary fails
            }
          }

        } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    }
  }, [selectedCycle]);

  // Handler for cycle selection
  const handleCycleChange = (event) => {
    const cycleId = parseInt(event.target.value);
    const selected = availableCycles.find(cycle => cycle.CycleID === cycleId);
    setSelectedCycle(selected);
  };

  // Generate summary stats from API data
  const summaryStats = dashboardData ? (() => {
    const stats = [
    {
      title: 'Overall Avg CI',
        value: dashboardData.summary?.overall_avg_ci || '0.00',
      icon: '🎯',
      borderColor: '#a855f7'
      },
    {
      title: 'Capabilities Tracked',
      value: dashboardData.summary?.total_category_count || '0',
      icon: '🛡️',
      borderColor: '#f97316'
    }
    ];

    return stats;
  })() : [];

  // Generate assessment summary cards separately
  const assessmentStats = assessmentSummary ? (() => {
    const submittedClick = () => {
      setShowSubmittedModal(true);
      setSubmittedPage(1);
    };

    const notSubmittedClick = () => {
      setShowNotSubmittedModal(true);
      setNotSubmittedPage(1);
    };

    return [
      {
        title: 'Assessments Submitted',
        value: assessmentSummary.submitted || '0',
        subtitle: `${assessmentSummary.submitted_percentage?.toFixed(1) || '0'}% of total`,
        icon: '✅',
        borderColor: '#22c55e',
        onClick: submittedClick,
        clickable: true
      },
      {
        title: 'Assessments Pending',
        value: assessmentSummary.not_submitted || '0',
        subtitle: `${assessmentSummary.not_submitted_percentage?.toFixed(1) || '0'}% of total`,
        icon: '⏳',
        borderColor: '#f97316',
        onClick: notSubmittedClick,
        clickable: true
      }
    ];
  })() : [];

  // Transform graph data from API to chart format
  const roleData = graphData?.bar_graph ? graphData.bar_graph.map((item) => ({
    role: item.Role,
    ci: parseFloat(item.AvgCIScore)
  })) : [];

  const capabilityData = graphData?.spider_chart ? graphData.spider_chart.map((item) => ({
    capability: item.CategoryName,
    score: parseFloat(item.AvgCIScore)
  })) : [];

  // Generate strengths and growth areas from API data
  const topStrengths = visualData?.strengths ? visualData.strengths.slice(0, 3).map((item, index) => ({
    name: item.CategoryName,
    score: parseFloat(item.AvgCIScore),
    percentage: Math.round((parseFloat(item.AvgCIScore) / 5) * 100)
  })) : [];

  const topGrowthAreas = visualData?.growth_areas ? visualData.growth_areas.slice(0, 3).map((item, index) => ({
    name: item.CategoryName,
    score: parseFloat(item.AvgCIScore),
    percentage: Math.round((parseFloat(item.AvgCIScore) / 5) * 100)
  })) : [];

  // Transform department data for pie chart and list
  const departmentDistribution = departmentData.length > 0 ? departmentData.map((dept, index) => {
    const colors = ['#3b82f6', '#a855f7', '#ec4899', '#f97316', '#22c55e', '#06b6d4', '#ef4444', '#84cc16', '#f59e0b', '#6366f1'];
    const totalMembers = departmentData.reduce((sum, d) => sum + (d.Members || 0), 0);
    const percentage = totalMembers > 0 ? Math.round(((dept.Members || 0) / totalMembers) * 100) : 0;

    return {
      name: dept.DepartmentName || 'Unknown Department',
      members: dept.Members || 0,
      ci: parseFloat(dept.AvgCI || 0),
      color: colors[index % colors.length],
      percentage: percentage
    };
  }) : [];

  return (
    <div style={styles.container} className="dashboard-container-wrapper">
      <GlobalHeader />
      {/* Assessment Summary Modals */}
      <AssessmentSummaryModal
        isOpen={showSubmittedModal}
        onClose={() => setShowSubmittedModal(false)}
        title={`Submitted Assessments (${assessmentSummary?.submitted || 0})`}
        employees={assessmentSummary?.submitted_employees || []}
        userRole={sessionStorage.getItem('access_role') || 'user'}
        currentPage={submittedPage}
        setCurrentPage={setSubmittedPage}
      />

      <AssessmentSummaryModal
        isOpen={showNotSubmittedModal}
        onClose={() => setShowNotSubmittedModal(false)}
        title={`Pending Assessments (${assessmentSummary?.not_submitted || 0})`}
        employees={assessmentSummary?.not_submitted_employees || []}
        userRole={sessionStorage.getItem('access_role') || 'user'}
        currentPage={notSubmittedPage}
        setCurrentPage={setNotSubmittedPage}
      />
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate('/Home')}>
          ← Back to Home
        </button>
        
        <div style={styles.headerContent}>
          <h1 style={styles.title}>Capability Index Dashboard</h1>
          <p style={styles.subtitle}>Comprehensive view of team capabilities and performance metrics</p>
        </div>

        <div style={styles.headerActions}>
          {/* Cycle Filter Dropdown */}
          <div style={styles.cycleFilter}>
            <label style={styles.cycleLabel}>Assessment Cycle:</label>
            <select
              className="cycle-select"
              value={selectedCycle?.CycleID || ''}
              onChange={handleCycleChange}
              disabled={loading || availableCycles.length === 0}
            >
              {availableCycles.map((cycle) => (
                <option key={cycle.CycleID} value={cycle.CycleID}>
                  {cycle.Name || cycle.CycleID}
                </option>
              ))}
            </select>
          </div>

          {sessionStorage.getItem('access_role') === 'admin' && (
            <div style={styles.advancedBtnContainer}>
              <button
                style={styles.advancedBtn}
                onClick={() => window.location.href = '/insights'}
              >
                📊 Advanced Insights
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p>Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>⚠️</div>
          <h3>Failed to Load Dashboard</h3>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={styles.retryButton}
          >
            Retry
          </button>
        </div>
      )}

      {/* Summary Cards - All in one line */}
      {!loading && !error && (
      <div style={styles.summaryGrid}>
        {summaryStats.map((stat, index) => (
          <div
            key={index}
            className="summary-card-hover"
            data-hover-color={stat.borderColor}
            style={{
              ...styles.summaryCard,
              cursor: stat.clickable ? 'pointer' : 'default'
            }}
            onClick={stat.onClick || (() => {})}
          >
            <div style={styles.statHeader}>{stat.title}</div>
            <div style={styles.statBottom}>
              <span style={{...styles.statIcon, color: stat.borderColor}}>{stat.icon}</span>
              <span style={styles.statValue}>{stat.value}</span>
            </div>
              {stat.subtitle && (
                <div style={styles.statSubtitle}>{stat.subtitle}</div>
              )}
          </div>
        ))}
        {assessmentStats.map((stat, index) => (
          <div
            key={`assessment-${index}`}
            className="assessment-card"
            data-hover-color={stat.borderColor}
            style={{
              ...styles.summaryCard,
              cursor: stat.clickable ? 'pointer' : 'default'
            }}
            onClick={stat.onClick || (() => {})}
          >
            <div style={styles.statHeader}>{stat.title}</div>
            <div style={styles.statBottom}>
              <span style={{...styles.statIcon, color: stat.borderColor}}>{stat.icon}</span>
              <span style={styles.statValue}>{stat.value}</span>
            </div>
              {stat.subtitle && (
                <div style={styles.statSubtitle}>{stat.subtitle}</div>
              )}
          </div>
        ))}
      </div>
      )}

      {/* Main Content */}
      {!loading && !error && (
        <>
      <div style={styles.mainContent}>
        <div style={styles.largeCard}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Average CI Score by Role</h3>
            <p style={styles.cardSubtitle}>Capability Index scores across different role levels</p>
          </div>
              {roleData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={roleData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis 
                dataKey="role" 
                tick={{fill: '#6b7280', fontSize: 12}} 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{fill: '#6b7280', fontSize: 12}} 
                domain={[0, 5]}
                ticks={[0, 1, 2, 3, 4, 5]}
              />
              <Tooltip 
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                wrapperStyle={{paddingTop: '20px'}}
                iconType="square"
              />
              <Bar 
                dataKey="ci" 
                fill="#3b82f6" 
                radius={[4, 4, 0, 0]}
                name="Avg CI Score"
              />
            </BarChart>
          </ResponsiveContainer>
              ) : (
                <div style={styles.emptyChartState}>
                  <p>No role data available for the selected cycle</p>
                </div>
              )}
        </div>
        
        <div style={styles.largeCard}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Team Capability Profile</h3>
            <p style={styles.cardSubtitle}>Average scores across all capability dimensions</p>
          </div>
              {capabilityData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={capabilityData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis 
                dataKey="capability" 
                tick={{fill: '#6b7280', fontSize: 11}}
              />
              <PolarRadiusAxis 
                domain={[0, 5]} 
                tick={{fill: '#6b7280', fontSize: 10}}
                angle={90}
              />
              <Radar 
                name="Score" 
                dataKey="score" 
                stroke="#a855f7" 
                fill="#a855f7" 
                fillOpacity={0.5} 
              />
              <Tooltip 
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
              ) : (
                <div style={styles.emptyChartState}>
                  <p>No capability data available for the selected cycle</p>
                </div>
              )}
        </div>
      </div>

      {/* Performance Breakdown */}
      <div style={styles.performanceGrid}>
        <div style={styles.breakdownCard}>
          <h3 style={styles.cardTitle}>Top 3 Strengths</h3>
          <p style={styles.cardSubtitle}>Highest performing capability areas</p>
          <div style={styles.strengthsList}>
                {topStrengths.length > 0 ? topStrengths.map((strength, index) => (
              <div key={index} style={styles.strengthItem}>
                <div style={styles.strengthBadge}>#{index + 1}</div>
                <div style={styles.strengthContent}>
                  <div style={styles.strengthName}>{strength.name}</div>
                      <div style={styles.strengthScore}>{strength.score.toFixed(2)}</div>
                </div>
                <div style={styles.strengthBar}>
                  <div 
                    style={{
                      ...styles.strengthBarFill,
                      width: `${strength.percentage}%`
                    }}
                  />
                </div>
              </div>
                )) : (
                  <div style={styles.emptyState}>
                    <p>No strength data available</p>
                  </div>
                )}
          </div>
        </div>
        
        <div style={styles.breakdownCard}>
          <h3 style={styles.cardTitle}>Top 3 Growth Areas</h3>
          <p style={styles.cardSubtitle}>Capabilities needing improvement</p>
          <div style={styles.growthList}>
                {topGrowthAreas.length > 0 ? topGrowthAreas.map((area, index) => (
              <div key={index} style={styles.growthItem}>
                <div style={styles.growthBadge}>#{index + 1}</div>
                <div style={styles.growthContent}>
                  <div style={styles.growthName}>{area.name}</div>
                      <div style={styles.growthScore}>{area.score.toFixed(2)}</div>
                </div>
                <div style={styles.growthBar}>
                  <div 
                    style={{
                      ...styles.growthBarFill,
                      width: `${area.percentage}%`
                    }}
                  />
                </div>
              </div>
                )) : (
                  <div style={styles.emptyState}>
                    <p>No growth area data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}


      {/* Department-wise Performance */}
      {!loading && !error && (
      <div style={styles.performanceGrid}>
        <div style={{...styles.teamCard, gridColumn: '1 / -1'}} className="department-distribution-card">
          <h3 style={styles.cardTitle}>Department-wise Performance</h3>
          <p style={styles.cardSubtitle}>Department size and average CI scores across the organization</p>
            <div style={styles.teamDistributionContent} className="department-distribution-content">
              <div style={styles.teamDistributionChart} className="department-distribution-chart">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart margin={{ top: 10, bottom: 10, left: 20, right: 20 }}>
                    <Pie
                      data={departmentDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props) => <CustomPieLabel {...props} maxLength={25} />}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="percentage"
                    >
                      {departmentDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value}%`,
                        'Percentage'
                      ]}
                      labelFormatter={(label) => `Department: ${label}`}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '8px 12px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
          <div style={styles.teamList} className="department-list">
            {departmentDistribution.length > 0 ? departmentDistribution.map((dept, index) => (
              <div key={index} style={styles.teamItem} className="department-item">
                <div style={{...styles.teamDot, background: dept.color}} className="department-dot" />
                <div 
                  style={styles.teamName} 
                  className="department-name"
                  title={dept.name.length > 20 ? dept.name : ''}
                >
                  {dept.name.length > 20 ? `${dept.name.substring(0, 20)}...` : dept.name}
                </div>
                <div style={styles.teamMembers} className="department-members">{dept.members} members</div>
                <div style={styles.teamCi} className="department-ci">CI: {dept.ci.toFixed(2)}</div>
                <div style={styles.teamProgress} className="department-progress">
                  <div
                    style={{
                      ...styles.teamProgressFill,
                      width: `${(dept.ci / 5) * 100}%`,
                      background: dept.color
                    }}
                  />
                </div>
              </div>
            )) : (
              <div style={styles.emptyState}>
                <p>No department data available for the selected cycle</p>
              </div>
            )}
          </div>
        </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    background: 'linear-gradient(to bottom, #F6FAFE, #F7FBFE)',
    minHeight: '100vh',
    padding: 0,
    margin: 0,
  },
  header: {
    background: '#ffffff',
    padding: '32px 40px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    marginTop:'70px',
  },
  backButton: {
    background: 'white',
    border: 'none',
    color: '#6b7280',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '8px 0',
    transition: 'color 0.2s ease',
    fontWeight: '400',
  },
  headerContent: {
    flex: 1,
    textAlign: 'center',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: '700',
    background: 'linear-gradient(90deg, #3b82f6 0%, #7c3aed 50%, #a855f7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6b7280',
    margin: 0,
    fontWeight: '400',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  cycleFilter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
  cycleLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cycleSelect: {
    background: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    minWidth: '200px',
    outline: 'none',
  },
  advancedBtnContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    paddingTop: '22px', // Match the label height (12px font ~16px line-height) + gap (4px) from cycleFilter
  },
  advancedBtn: {
    background: '#1f2937',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.2s ease',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '20px',
    padding: '0 40px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  assessmentGrid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    padding: '0 40px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
    flexWrap: 'wrap',
  },
  summaryCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    transition: 'all 0.3s ease',
    minWidth: '260px',
    flex: '1 1 0',
  },
  assessmentCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    transition: 'all 0.3s ease',
    width: '400px',
    minWidth: '350px',
  },
  statHeader: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statBottom: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    fontSize: '32px',
    lineHeight: '1',
  },
  statValue: {
    fontSize: '2.25rem',
    fontWeight: '700',
    color: '#1e293b',
    lineHeight: '1.2',
  },
  statSubtitle: {
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '4px',
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
    padding: '0 40px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  largeCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  cardHeader: {
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 4px 0',
  },
  cardSubtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: 0,
    fontWeight: '400',
  },
  performanceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '20px',
    padding: '0 40px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  teamCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  breakdownCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  strengthsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  growthList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '20px',
  },
  strengthItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '6px',
    background: '#f9fafb',
  },
  growthItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '6px',
    background: '#f9fafb',
  },
  strengthBadge: {
    background: '#22c55e',
    color: 'white',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
  },
  growthBadge: {
    background: '#f97316',
    color: 'white',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
  },
  strengthContent: {
    flex: 1,
  },
  growthContent: {
    flex: 1,
  },
  strengthName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f2937',
    marginBottom: '2px',
  },
  growthName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f2937',
    marginBottom: '2px',
  },
  strengthScore: {
    fontSize: '12px',
    color: '#6b7280',
  },
  growthScore: {
    fontSize: '12px',
    color: '#6b7280',
  },
  strengthBar: {
    width: '100px',
    height: '8px',
    background: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  growthBar: {
    width: '100px',
    height: '8px',
    background: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  strengthBarFill: {
    height: '100%',
    background: '#22c55e',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  growthBarFill: {
    height: '100%',
    background: '#f97316',
    borderRadius: '4px',
    transition: 'width 0.5s ease',
  },
  teamDistributionContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginTop: '12px',
  },
  teamDistributionChart: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  teamList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  teamItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    borderRadius: '6px',
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    transition: 'background 0.2s ease',
  },
  teamDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  teamName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e293b',
    minWidth: '160px',
    flex: '1',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  teamMembers: {
    fontSize: '12px',
    color: '#64748b',
    minWidth: '90px',
    textAlign: 'right',
  },
  teamCi: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1e293b',
    minWidth: '60px',
    textAlign: 'right',
  },
  teamProgress: {
    flex: 1,
    height: '6px',
    background: '#e5e7eb',
    borderRadius: '3px',
    overflow: 'hidden',
    minWidth: '100px',
    maxWidth: '150px',
  },
  teamProgressFill: {
    height: '100%',
    borderRadius: '5px',
    transition: 'width 0.5s ease',
  },

  /* Empty State */
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b7280',
    fontSize: '14px',
  },

  /* Loading and Error States */
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '40px',
    color: '#6b7280',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '40px',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  retryButton: {
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '16px',
    transition: 'background 0.2s ease',
  },

  /* Empty State */
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b7280',
  },
  emptyChartState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    color: '#6b7280',
    fontSize: '14px',
    background: '#f8fafc',
    borderRadius: '8px',
    border: '1px dashed #e5e7eb',
  },

};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: 0,
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '800px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#111827',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
  },
  body: {
    padding: '24px',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    marginBottom: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
  },
  tableHeaderCell: {
    padding: '12px 16px',
    textAlign: 'center',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '1px solid #e5e7eb',
  },
  tableCell: {
    padding: '12px 16px',
    color: '#374151',
    textAlign: 'center',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    gap: '12px',
  },
  searchContainer: {
    flex: 1,
  },
  searchInput: {
    width: '100%',
    padding: '10px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  searchInputFocus: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  exportButton: {
    padding: '10px 20px',
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
  },
  exportButtonDisabled: {
    backgroundColor: '#d1d5db',
    cursor: 'not-allowed',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#6b7280',
    fontSize: '14px',
  },
  paginationContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  paginationInfo: {
    fontSize: '14px',
    color: '#6b7280',
    display: 'flex',
    alignItems: 'center',
  },
  rowsPerPageSelect: {
    padding: '4px 8px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  pageNumbers: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  pageNumber: {
    minWidth: '32px',
    height: '32px',
    padding: '0 8px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    background: 'white',
    color: '#6b7280',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activePageNumber: {
    background: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6',
  },
  pageEllipsis: {
    padding: '0 4px',
    color: '#6b7280',
  },
  tableRow: {
    borderBottom: '1px solid #f3f4f6',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
  },
  pageButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s',
  },
  disabledButton: {
    backgroundColor: '#d1d5db',
    cursor: 'not-allowed',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#6b7280',
  },
};

export default Dashboard;